import React from "react";
import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import DateSelect from "../../core/DateSelect";
import InputGroup from "../../core/InputGroup";
import styles from "./processFeeDetailsDialog.module.scss";
import CloseIcon from '@mui/icons-material/Close';
import { ProcessFeeDetailsResponseData, ProcessFeeOption } from "../../../apis/models/ProcessFeeDetailsResponse";
import { dateToString, isInvalidDate, nowAsString } from "../../../libs/helpers";
import { ProcessFeeOptionRequestItem } from "../../../apis/models/ProcessFeeRequest";
import { BrandlistResponseData } from "../../../apis/models/BrandlistResponse";
import { InvoiceListResponseData, InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import { format } from 'date-fns';
import { isValid } from "date-fns/esm";
import ProcessFeeOptionSelect from "../../core/ProcessFeeOptionSelect";

type InputField = {
  dateAdded: string,
  productNumber: string,
  workDetailOption?: ProcessFeeOption,
  quantity: string,
  unitPrice: string,
}

type ErrorInfo = {
  dateAdded?: string;
  workDetails?: string;
  quantity?: string;
}

type RowType = InputField & {
  error: ErrorInfo
}

const initialItem: RowType = {
  dateAdded: nowAsString(),
  productNumber: "",
  quantity: "",
  unitPrice: "0",
  error: {},
}

const ProcessFeeDetailsDialog = (props: ProcessFeeDetailsDialogProps) => {
  const {open, onClose, onCancel, onSave, processFeeOptions, targetBrand, targetInvoice, targetInvoices, onAddOptions} = props;
  const [addedItems, setAddedItems] = React.useState<RowType[]>([initialItem]);

  const addItem = () => {
    setAddedItems([...addedItems, initialItem]);
  }

  const deleteItem = (index: number) => {
    if (addedItems.length <= 1) {
      return
    }
    const newItems = [...addedItems];
    newItems.splice(index,1);
    setAddedItems(newItems);
  }

  const editItem = (item: RowType, index: number) => {
    if (Object.keys(item.error).length > 0) {
      item.error = validateRow(item)
    }
    const newItems = [...addedItems];
    newItems.splice(index, 1, item);
    setAddedItems(newItems);
  }

  const handleSelectOption = (option: ProcessFeeOption | undefined, index: number) => {
    const newItem = {...addedItems[index]};
    newItem.workDetailOption = option;
    newItem.unitPrice = option ? String(option.processFeeOption.unitPrice) : "0";

    editItem(newItem, index);
  }

  const validateRow = (item: RowType) => {
    const error: ErrorInfo = {
      dateAdded: validateDateAdded(item.dateAdded),
      workDetails: item.workDetailOption ? undefined : "作業内容を選択してください",
      quantity: validateNumber(item.quantity),
    }
    return error
  }

  const validateDateAdded = (value: string) => {
    if (value.length === 0) {
      return "入力必須です"
    } else if (isInvalidDate(value)) {
      return "年月日で入力してください"
    } else if (!targetInvoice && !getInvoiceAtDate(value)) {
      return "請求期間の範囲で入力してください"
    } else if (targetInvoice) {
      const invoiceFrom = format(new Date(targetInvoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(targetInvoice.to), "yyyy/MM/dd")
      if (value < invoiceFrom || invoiceTo < value) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }
  
  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return "入力必須です"
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const getInvoiceAtDate = (value: string) => {
    if (!targetInvoices || targetInvoices.length <= 0) {
      return undefined
    }
    return targetInvoices.find(invoice => {
      const invoiceFrom = format(new Date(invoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(invoice.to), "yyyy/MM/dd")
      return (invoiceFrom <= value  && value <= invoiceTo)
    })
  }

  const saveAddedItem = () => {
    const containErrors = addedItems.some((item, index) => {
      const err = validateRow(item)
      item.error = err
      const newItems = [...addedItems];
      newItems.splice(index, 1, item);
      setAddedItems(newItems);
      return err.dateAdded || err.quantity || err.workDetails
    })

    if (addedItems.length <= 0 || containErrors) {
      return
    }
    const formatData: ProcessFeeDetailsResponseData[] = [];
    addedItems.forEach(item => {
      let invoiceMasterId;
      if (targetInvoice) {
        invoiceMasterId = targetInvoice.invoiceMasterId
      } else {
        invoiceMasterId = getInvoiceAtDate(item.dateAdded)?.invoiceMasterId
      }
      if (!invoiceMasterId) {
        return
      }

      const dataItem: ProcessFeeDetailsResponseData = {
        invoiceMasterId: invoiceMasterId,
        dateAdded: item.dateAdded,
        productNumber: item.productNumber,
        quantity: parseInt(item.quantity),
        processFeeId: "",
        processFeeOptionId: item.workDetailOption?.processFeeOptionId ?? "",
        processFeeOption: item.workDetailOption?.processFeeOption!,
        created: "",
        modified: "",
      }
      formatData.push(dataItem);
    })
    onSave && onSave(formatData);
    setAddedItems([initialItem]);
  }

  const invoicePeriod = React.useMemo(() => {
    if (targetInvoices && targetInvoices.length > 0) {
      const min = targetInvoices.reduce((prev, i) => prev < i.from ? prev : i.from , "3000-12-31")
      const max = targetInvoices.reduce((prev, i) => prev > i.to ? prev : i.to , "")
      return `${format(new Date(min), "yyyy/MM/dd")} ～ ${format(new Date(max), "yyyy/MM/dd")}`
    } else if (targetInvoice){
      return `${format(new Date(targetInvoice.from), "yyyy/MM/dd")} ～ ${format(new Date(targetInvoice.to), "yyyy/MM/dd")}`
    }
    return ''
  }, [targetInvoices, targetInvoice])

  // This function is used because only the error object is not initialized.
  const initializeValue = () => {
    const initialValue = ({...initialItem, error: {}})
    setAddedItems([initialValue])
  }

  React.useEffect(() => {
    initializeValue();
  }, [])

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth={true} maxWidth="lg" PaperProps={{sx: {height: '90%'}}}>
      <DialogTitle sx={{fontWeight: 700, fontSize: "24px", paddingLeft: "40px"}}>{targetBrand?.brandName ?? ''} - 加工費追加</DialogTitle>
      <DialogContent sx={{padding: "0 40px 24px", display: "flex", flexDirection: "column"}}>
          <div className={styles.dialog__label}>
            <span>請求期間：</span>
            <span className={styles.dialog__label_content}>{invoicePeriod}</span>
          </div>
          <div className={styles.dialog__content}>
            {
              Array.isArray(addedItems) && addedItems.length > 0 && addedItems.map((item, index) => (
                <div className={styles.dialog__content_row} key={index}>
                  <DateSelect
                    handleDateChange={(date) => editItem({...item, dateAdded: isValid(date) ? dateToString(date) : ""}, index)}
                    selectedDate={item.dateAdded !== "" ? new Date(item.dateAdded) : null}
                    label="日付"
                    required
                    errMess={item.error.dateAdded}
                  />
                  <InputGroup label="品番" type="text" onChange={(val) => editItem({...item, productNumber: val}, index)} value={item.productNumber}/>
                  <div>
                    <div className={styles.dropdown_label}>作業内容<span>*</span></div>
                    <ProcessFeeOptionSelect
                      options={processFeeOptions}
                      onSelect={(value) => handleSelectOption(value, index)}
                      onAddOption={(value) => onAddOptions(value)}
                      errorMessage={item.error.workDetails}
                    />
                  </div>
                  <InputGroup
                    label="加工単価"
                    required
                    type="number"
                    disabled
                    onChange={() => {}}
                    value={item.unitPrice}
                  />
                  <InputGroup
                    label="枚数"
                    required
                    type="number"
                    onChange={(val) => editItem({...item, quantity: val}, index)}
                    value={item.quantity}
                    error={!!item.error.quantity} errorMessage={item.error.quantity}
                  />
                  <div className={styles.close_holder}><CloseIcon color="primary" className={styles.delete_icon} onClick={() => deleteItem(index)}/></div>
                </div>
              ))
            }
          </div>
          <div className={styles.dialog__add}>
              <Button variant="outlined" color="primary" onClick={() => addItem()}>＋行追加</Button>
          </div>
          <div className={styles.dialog__functions}>
              <Button variant="outlined" color="primary" onClick={() => onCancel && onCancel()}>キャンセル</Button>
              <Button variant="contained" color="primary" onClick={() => saveAddedItem()}>登録</Button>
          </div>
      </DialogContent>
    </Dialog>
  )
}

export default ProcessFeeDetailsDialog;

export type ProcessFeeDetailsDialogProps = {
  open: boolean,
  onClose: () => void,
  onCancel?: () => void,
  onSave: (items: ProcessFeeDetailsResponseData[]) => void,
  processFeeOptions: ProcessFeeOption[],
  targetBrand?: BrandlistResponseData,
  targetInvoice?: InvoiceMasterData,
  targetInvoices?: InvoiceListResponseData[],
  from?: "brandlist" | "invdetails",
  onAddOptions: (val:ProcessFeeOptionRequestItem) => void
}